import { Component, OnInit } from '@angular/core';
import { NbDialogRef } from '@nebular/theme';
import { EnforcementService } from '../../../shared/enforcement/enforcement.service';
import { ManagedUser, EnforcementSettings } from '../../../shared/enforcement/enforcement.types';

@Component({
  selector: 'ngx-access-manager',
  templateUrl: './access-manager.component.html',
  styleUrls: ['./access-manager.component.scss']
})
export class AccessManagerComponent implements OnInit {
  loading = true;
  error = false;
  users: ManagedUser[] = [];
  filtered: ManagedUser[] = [];
  busyId: number | null = null;
  settings: EnforcementSettings | null = null;
  settingsBusy: 'master' | 'rule' | null = null;
  private query = '';

  constructor(
    protected dialogRef: NbDialogRef<AccessManagerComponent>,
    private enforcementService: EnforcementService
  ) {}

  ngOnInit(): void {
    this.loadSettings();
    this.load();
  }

  loadSettings(): void {
    this.enforcementService.getEnforcementSettings().subscribe({
      next: (settings) => (this.settings = settings),
      error: () => {}
    });
  }

  toggleEnforcement(): void {
    if (!this.settings || this.settingsBusy) return;
    this.settingsBusy = 'master';
    this.enforcementService
      .updateEnforcementSettings({ enforcementEnabled: !this.settings.enforcementEnabled })
      .subscribe({
        next: (settings) => {
          this.settings = settings;
          this.settingsBusy = null;
          this.load();
        },
        error: () => (this.settingsBusy = null)
      });
  }

  toggleRule(): void {
    if (!this.settings || this.settingsBusy) return;
    this.settingsBusy = 'rule';
    this.enforcementService
      .updateEnforcementSettings({
        pendingRequestsRuleEnabled: !this.settings.pendingRequestsRuleEnabled
      })
      .subscribe({
        next: (settings) => {
          this.settings = settings;
          this.settingsBusy = null;
          this.load();
        },
        error: () => (this.settingsBusy = null)
      });
  }

  load(): void {
    this.loading = true;
    this.error = false;
    this.enforcementService.listManagedUsers().subscribe({
      next: (users) => {
        this.users = users;
        this.applyFilter();
        this.loading = false;
      },
      error: () => {
        this.error = true;
        this.loading = false;
      }
    });
  }

  onSearch(event: Event): void {
    this.query = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.applyFilter();
  }

  private applyFilter(): void {
    if (!this.query) {
      this.filtered = this.users;
      return;
    }
    this.filtered = this.users.filter(
      (u) =>
        u.name.toLowerCase().includes(this.query) ||
        String(u.userId).includes(this.query)
    );
  }

  lock(user: ManagedUser): void {
    if (this.busyId) return;
    this.busyId = user.userId;
    this.enforcementService.adminLock(user.userId).subscribe({
      next: () => this.refresh(),
      error: () => (this.busyId = null)
    });
  }

  unlock(user: ManagedUser): void {
    if (this.busyId) return;
    this.busyId = user.userId;
    this.enforcementService.adminUnlock(user.userId).subscribe({
      next: () => this.refresh(),
      error: () => (this.busyId = null)
    });
  }

  private refresh(): void {
    this.enforcementService.listManagedUsers().subscribe({
      next: (users) => {
        this.users = users;
        this.applyFilter();
        this.busyId = null;
      },
      error: () => (this.busyId = null)
    });
  }

  statusLabel(u: ManagedUser): string {
    if (u.released) return 'Released';
    if (u.locked) return u.lockType === 'manual' ? 'Locked · manual' : 'Locked · overdue';
    return 'Active';
  }

  statusClass(u: ManagedUser): string {
    if (u.released) return 'is-released';
    if (u.locked) return 'is-locked';
    return 'is-active';
  }

  close(): void {
    this.dialogRef.close();
  }

  trackByUser(_i: number, u: ManagedUser): number {
    return u.userId;
  }
}
